import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { TPnLData } from '@shared/types/frontend/assetPage';
import { toJpeg } from 'html-to-image';
import { FacebookShareButton, TelegramShareButton, TwitterShareButton, WhatsappShareButton } from 'react-share';
import { IoCheckmark, IoShareSocialSharp } from 'react-icons/io5';
import { toast } from 'sonner';
import { Copy } from 'lucide-react';
import { FaFacebook, FaTelegram, FaWhatsapp } from 'react-icons/fa';
import { RxTwitterLogo } from 'react-icons/rx';
import { toSpecialFixed } from '@/lib/misc/utils';
import Loader from '@/components/shared/Loader';
import getNetworkLogo from '@/lib/misc/getNetworkLogo';
import classNames from '@/lib/misc/classNames';
import { Button } from '@/components/ui/button';
import MobileReferralQr from './MobileReferralQr';
import SlidingModal from '../home/mobile/SlidingModal';
import getCurrentGains from '@/lib/pnl/getCurrentGains';
import { useTokenData } from '@/hooks/asset/useTokenData';
import Loading from '../shared/Loading';
import { PreloadedTokenImage } from '../portfolio/desktop/DTFlexYourGains';
import { geniusData } from '../auth/const';
export default function FlexYourGains({
  tokenData,
  tokenAddressInput,
  network,
  tokenPnLData,
  isLoading
}: {
  tokenData?: any;
  tokenAddressInput?: string;
  network?: number;
  tokenPnLData: TPnLData;
  isLoading: boolean;
}) {
  const [networkUrl, setNetworkUrl] = useState(null);
  const [tokenUrl, setTokenUrl] = useState(null);
  const [preloadedTokenImage, setPreloadedTokenImage] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const [pnlLink, setPnlLink] = useState(null);
  const [savingToS3, setSavingToS3] = useState(false);
  const [isOpenShare, setIsOpenShare] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const {
    data: tokenDataFallBack
  } = useTokenData({
    tokenAddress: tokenAddressInput,
    tokenNetwork: network?.toString(),
    refetchInterval: 60000,
    staleTime: 60000
  });
  tokenData = tokenDataFallBack || tokenData;
  if (tokenData) {
    const ogUrl = tokenData?.logo_urls?.token_logo_url;
    tokenData['logo_urls'] = {};
    tokenData['logo_urls']['token_logo_url'] = ogUrl || tokenData?.token?.info?.imageLargeUrl || tokenData?.token?.info?.imageThumbUrl;
  }
  function toggleShare() {
    setIsOpenShare(!isOpenShare);
  }
  useEffect(() => {
    if (!tokenData?.logo_urls?.token_logo_url) return;
    const proxyUrl = `/api/proxy-image?url=${encodeURIComponent(tokenData?.logo_urls?.token_logo_url)}`;
    fetch(proxyUrl).then(response => response.blob()).then(blob => {
      const objectUrl = URL.createObjectURL(blob);
      setPreloadedTokenImage(objectUrl);
    }).catch(error => {
      console.error('Error preloading token image:', error);
      setPreloadedTokenImage(null);
    });
    return () => {
      if (preloadedTokenImage) {
        URL.revokeObjectURL(preloadedTokenImage);
      }
    };
  }, [tokenData?.logo_urls?.token_logo_url]);
  const handleShare = useCallback(async () => {
    if (pnlLink) {
      return;
    }
    try {
      toggleShare();
      let dataUrl = '';
      const minDataLength = 60000;
      let i = 0;
      const maxAttempts = 7;
      dataUrl = await toJpeg(ref.current, {
        quality: 0.9
      });
      while (i < maxAttempts) {
        dataUrl = await toJpeg(ref.current, {
          quality: 0.9
        });
        i += 1;
      }
      console.log('dataUrl', dataUrl.length);
      if (dataUrl.length < minDataLength) {
        throw new Error('Error creating pnl image');
      }
      const link = document.createElement('a');
      link.download = 'my-pnl.jpeg';
      link.href = dataUrl;
      // link.click();

      // Upload to S3
      setSavingToS3(true);
      const response = await fetch('/api/upload-to-s3', {
        method: 'POST',
        body: JSON.stringify({
          mod: 'pnl',
          image: dataUrl,
          metadata: {
            tokenData,
            tokenPnLData
          }
        })
      });
      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }
      const result = await response.json();
      setPnlLink(`${window?.location?.origin}${result.ulr}`);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setSavingToS3(false);
      toggleShare();
    }
  }, [ref, tokenData, tokenPnLData, toggleShare]);
  const randomGeniusQuote = useMemo(() => geniusData[Math.floor(Math.random() * geniusData.length)], []);
  useEffect(() => {
    if (window) {
      const href = window.location.href;
      if (href?.includes('localhost')) {
        tokenData?.logo_urls?.token_logo_url;
        const localTokenUrl = tokenData?.logo_urls?.token_logo_url?.replace('https://www.tradegenius.com/', '/');
        setTokenUrl(localTokenUrl);
        const networkLocal = getNetworkLogo(tokenData?.network || tokenData?.token?.networkId || 1).replace('https://www.tradegenius.com/', '/');
        setNetworkUrl(networkLocal);
      } else {
        setTokenUrl(tokenData?.logo_urls?.token_logo_url);
        setNetworkUrl(getNetworkLogo(tokenData?.network || tokenData?.token.networkId || 1));
      }
    }
  }, [tokenData]);
  const handleCopy = (textToCopy: string) => {
    if (textToCopy) {
      navigator.clipboard.writeText(textToCopy).then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 1000);
      }).catch(() => {
        toast.error('Failed to copy!');
      });
      toast.success('Copied to clipboard!');
    }
  };
  const titleString = `I just made ${getCurrentGains({
    tokenPnLData
  })?.string} (${tokenPnLData?.actualPnLChangePercent?.toFixed(2)}%) on ${tokenData?.contract_ticker_symbol} using @GeniusTerminal! 🎉`;
  return <div className='pb-4' data-sentry-component="FlexYourGains" data-sentry-source-file="FlexYourGains.tsx">
      {isLoading ? <Loader /> : <>
          <div className='overflow-hidden justify-center flex rounded-[20px]'>
            <div className='w-full'>
              <div ref={ref} className='gainsImage overflow-hidden DtflexGains rounded-[20px]'>
                <div className='p-4 rounded-xl'>
                  <div className='GainsBox p-4 rounded-[20px] border-genius-blue border-[1px]'>
                    <div>
                      <div className='flex items-center'>
                        <div className='relative rounded-full  w-fulls flex items-center'>
                          <div className='relative inline-block'>
                            <PreloadedTokenImage tokenUrl={tokenUrl || 'https://tradegenius.com/static/genius.png'} size={28} className='rounded-full' />
                            <div className='absolute bottom-0 right-0'>
                              <img src={networkUrl || '/static/genius.png'} width={100} height={100} alt='Badge' className={`w-[${12}px] h-[${12}px] rounded-full bg-black`} />
                            </div>
                          </div>
                          <p className='text-[22px] ml-3'>
                            {(tokenData?.contract_name || tokenData?.token?.name || '-')?.replace('Wrapped SOL', 'Solana')}
                          </p>
                        </div>
                      </div>
                      <p className='text-[14px] text-genius-cream/50 flex'>
                        {tokenData?.contract_ticker_symbol || tokenData?.token?.symbol || '-'}
                      </p>
                      <div className='justify-start'>
                        <p className={classNames('text-[40px] text-green-400', tokenPnLData?.actualPnLChangePercent < 0 && 'text-red-400')}>
                          {(tokenPnLData?.actualPnLChangePercent || 0)?.toFixed(2)}%
                        </p>
                        <p className={classNames('text-[16px] text-green-400', tokenPnLData?.actualPnLChangePercent < 0 && 'text-red-400')}>
                          {/* {getCurrentGains({ tokenPnLData })?.string} */}
                          <p className={classNames('text-xs flex items-center justify-start text-white')}>
                            <div className='flex flex-col'>
                              <i>{randomGeniusQuote?.quote}</i>- {randomGeniusQuote?.quoteBy}
                            </div>

                            {/* {getCurrentGains({ tokenPnLData })?.string} */}
                          </p>
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className='flex items-center w-full gap-2 mt-2'>
                    {/* SECOND BOX  */}
                    <div className='GainsBox w-full rounded-[16px] p-3 flex items-center justify-between text-genius-cream border-genius-blue border-[1px]'>
                      <div>
                        <p className='text-[10px] text-genius-gradient'>Entry Price</p>
                        <p className='text-[10px] text-genius-gradient'>Current Price</p>
                        {/* <p className='text-[10px]'>Exit value</p> */}
                      </div>
                      <div className='ta-r'>
                        <p className='text-[10px]'>
                          ${toSpecialFixed(tokenPnLData?.avgBuyPrice, 2).value}
                        </p>
                        <p className='text-[10px]'>${toSpecialFixed(tokenPnLData?.LTP, 4).value}</p>
                        {/* <p className='text-[24px]'>4.8 SOL</p> */}
                      </div>
                    </div>
                  </div>
                </div>
                <MobileReferralQr />
              </div>
              <Button onClick={() => {
            console.log('s3wefwfwf button clicked');
            handleShare();
          }} className={`mt-4 w-full h-[56px] mb-2 genius-shadow hover:bg-genius-pink text-[16px] rounded-full bg-genius-pink text-genius-blue flex items-center justify-center gap-2`}>
                <IoShareSocialSharp className='w-[20px] h-[20px]' />
                Share
              </Button>
              <SlidingModal heightFit={true} isOpen={isOpenShare} onClose={toggleShare} className='bg-genius-'>
                {savingToS3 || !pnlLink ? <div className='flex flex-col items-center justify-center'>
                    <div className='mb-4'>Generating PnL link...</div>
                    <Loading />
                  </div> : <div className='mt-4 ta-c justify-center px-2'>
                    <h1 className='text-genius-gradient text-[24px]'>Share Profits</h1>
                    <p className='text-[14px] text-genius-gradient px-8'>
                      Anyone who clicks on this card link will also be signed up using your referral
                      code.
                    </p>
                    <div onClick={() => handleCopy(pnlLink)} className='mt-6 h-[38px] text-[12px] text-genius-blue rounded-full w-full px-4 flex items-center justify-between bg-genius-pink'>
                      <p>{pnlLink.slice(0, 50)}...</p>
                      <div className='rounded-full p-2 h-8 w-8 flex items-center justify-center'>
                        {isCopied ? <IoCheckmark className={'w-[15px] right-4 text-genius-blue'} /> : <Copy className={'w-[15px] cursor-pointer text-genius-blue'} />}
                      </div>
                    </div>
                    <div className='relative w-full h-fit'>
                      <div className='w-full ta-c justify-center mt-4 space-y-1'>
                        <FacebookShareButton url={pnlLink} hashtag='#TradeGenius' className='w-full'>
                          <div className='h-[56px] w-full rounded-full bg-genius-blue flex items-center px-4'>
                            <FaFacebook className='w-[15px] h-[15px]' />
                            <p className='text-[12px] ml-2'>Facebook</p>
                          </div>
                        </FacebookShareButton>

                        <TwitterShareButton url={pnlLink} title={titleString} className='w-full'>
                          <div className='h-[56px] w-full rounded-full bg-genius-blue flex items-center px-4'>
                            <RxTwitterLogo className='w-[15px] h-[15px]' />
                            <p className='text-[12px] ml-2'>Twitter (X)</p>
                          </div>
                        </TwitterShareButton>

                        <TelegramShareButton url={pnlLink} title={titleString} className='w-full'>
                          <div className='h-[56px] w-full rounded-full bg-genius-blue flex items-center px-4'>
                            <FaTelegram className='w-[15px] h-[15px]' />
                            <p className='text-[12px] ml-2'>Telegram</p>
                          </div>
                        </TelegramShareButton>

                        <WhatsappShareButton url={pnlLink} title={titleString} className='w-full'>
                          <div className='h-[56px] w-full rounded-full bg-genius-blue flex items-center px-4'>
                            <FaWhatsapp className='w-[15px] h-[15px]' />
                            <p className='text-[12px] ml-2'>WhatsApp</p>
                          </div>
                        </WhatsappShareButton>
                      </div>
                    </div>
                  </div>}
              </SlidingModal>
            </div>
          </div>
        </>}
    </div>;
}